import React, { useState, useEffect } from 'react';
import { supabase } from '../../api/supabase';
import {
  Select,
  message as antMessage,
  Input,
  Radio,
  DatePicker,
  TimePicker,
  Checkbox,
  Spin
} from 'antd';
import dayjs from 'dayjs';
import {
  CopyOutlined,
  CheckOutlined,
  CoffeeOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  RocketOutlined,
  FileTextOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  FormOutlined,
  IdcardOutlined,
  LoadingOutlined,
  CarOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { colors, components, segmentBtn } from '../../styles/monochromeTheme';

const { Option } = Select;

const Shortcuts = () => {
  const [boats, setBoats] = useState([]);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [selectedCharterType, setSelectedCharterType] = useState(null);
  const [copiedItem, setCopiedItem] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load data from localStorage
  const [language, setLanguage] = useState(() => localStorage.getItem('shortcuts_language') || 'fr');
  const [selectedSignature, setSelectedSignature] = useState(() => localStorage.getItem('shortcuts_signature') || 'Pierre');
  const [selectedAction, setSelectedAction] = useState(() => localStorage.getItem('shortcuts_action') || 'payment');

  const [vesselUpgradeData, setVesselUpgradeData] = useState(() => {
    const saved = localStorage.getItem('shortcuts_vesselUpgradeData');
    return saved ? JSON.parse(saved) : {
      recipientType: 'client',
      clientName: '',
      agentName: '',
      vesselName: '',
      departureFrom: 'SBH',
      destinationTo: 'SXM',
      date: '',
      isTomorrow: true,
      timeType: 'preset',
      presetTime: '09:00',
      customTime: '',
      serviceType: 'Transfer',
      numberOfGuests: '2'
    };
  });

  const [jetskiData, setJetskiData] = useState(() => {
    const saved = localStorage.getItem('shortcuts_jetskiData');
    return saved ? JSON.parse(saved) : {
      customerType: 'resident',
      duration: '1h',
      customPrice: ''
    };
  });

  const [cashSheetData, setCashSheetData] = useState(() => {
    const saved = localStorage.getItem('shortcuts_cashSheetData');
    return saved ? JSON.parse(saved) : { includeInvoices: false };
  });

  const [confirmationData, setConfirmationData] = useState(() => {
    const saved = localStorage.getItem('shortcuts_confirmationData');
    return saved ? JSON.parse(saved) : { askLunchInfo: false };
  });

  // Booking Confirmation data (for charter/sortie)
  const [bookingConfirmData, setBookingConfirmData] = useState(() => {
    const saved = localStorage.getItem('shortcuts_bookingConfirmData');
    return saved ? JSON.parse(saved) : {
      clientName: '',
      time: '09:00',
      outingType: 'fullDay',
      numberOfGuests: '2',
      isTomorrow: true,
      date: '',
      askLunchInfo: false
    };
  });

  // Transfer Confirmation data
  const [transferConfirmData, setTransferConfirmData] = useState(() => {
    const saved = localStorage.getItem('shortcuts_transferConfirmData');
    return saved ? JSON.parse(saved) : {
      clientName: '',
      transferName: '',
      flightNumber: '',
      time: '09:00',
      numberOfGuests: '2',
      isTomorrow: true,
      date: '',
      departureFrom: '',
    };
  });

  const locations = [
    { code: 'SBH', name: 'St Barth' },
    { code: 'SXM', name: 'St Martin' },
    { code: 'AXA', name: 'Anguilla' }
  ];

  // Save to localStorage on changes
  useEffect(() => { localStorage.setItem('shortcuts_language', language); }, [language]);
  useEffect(() => { localStorage.setItem('shortcuts_signature', selectedSignature); }, [selectedSignature]);
  useEffect(() => { localStorage.setItem('shortcuts_action', selectedAction); }, [selectedAction]);
  useEffect(() => { localStorage.setItem('shortcuts_vesselUpgradeData', JSON.stringify(vesselUpgradeData)); }, [vesselUpgradeData]);
  useEffect(() => { localStorage.setItem('shortcuts_jetskiData', JSON.stringify(jetskiData)); }, [jetskiData]);
  useEffect(() => { localStorage.setItem('shortcuts_cashSheetData', JSON.stringify(cashSheetData)); }, [cashSheetData]);
  useEffect(() => { localStorage.setItem('shortcuts_confirmationData', JSON.stringify(confirmationData)); }, [confirmationData]);
  useEffect(() => { localStorage.setItem('shortcuts_bookingConfirmData', JSON.stringify(bookingConfirmData)); }, [bookingConfirmData]);
  useEffect(() => { localStorage.setItem('shortcuts_transferConfirmData', JSON.stringify(transferConfirmData)); }, [transferConfirmData]);
  useEffect(() => { fetchBoats(); }, []);

  const fetchBoats = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('yachts')
        .select('id, name, brand, length_feet, charter_packages')
        .order('name');
      if (error) throw error;
      setBoats(data || []);
      if (data && data.length > 0) setSelectedBoat(data[0]);
    } catch (error) {
      antMessage.error(`Erreur lors du chargement des bateaux: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, itemId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemId);
      antMessage.success(language === 'fr' ? 'Texte copié !' : 'Text copied!');
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      antMessage.error(language === 'fr' ? 'Erreur lors de la copie' : 'Copy error');
    }
  };

  // Translations
  const translations = {
    fr: {
      title: 'Raccourcis',
      actions: 'Actions',
      boat: 'Bateau',
      outingType: 'Type de sortie',
      copyMessage: 'Copier le message',
      copied: 'Copié !',
      paymentAction: 'Lien de Paiement',
      alcoholAction: 'Package Alcool',
      invoiceAction: 'Envoi Facture',
      confirmationAction: 'Confirmation Paiement',
      vesselUpgradeAction: 'Surclassement Bateau',
      jetskiAction: 'Réservation Jetski',
      contractAction: 'Envoi Contrat',
      cashSheetAction: 'Feuille de Caisse',
      passportsAction: 'Passeports',
      bookingConfirmAction: 'Confirm. Sortie',
      transferConfirmAction: 'Confirm. Transfert',
      outingType: 'Type de sortie',
      fullDay: 'Journée complète',
      halfDayAM: 'Demi-journée matin',
      halfDayPM: 'Demi-journée après-midi',
      sunset: 'Sunset',
      bookingConfirmMessage: 'Cher/Chère {clientName},\n\nNous avons le plaisir de vous confirmer votre {outingTypeText} {dateText} à partir de {time} depuis :\n\n📍 Point de rendez-vous : 14 Rue Jeanne d\'Arc, Gustavia\n\nHeure : {time}\nNombre de passagers : {numberOfGuests}{lunchQuestion}\nToutes les autres informations de votre réservation restent inchangées. Si vous avez des questions, n\'hésitez pas à nous contacter.\n\n\nNous sommes impatients de vous accueillir à bord !\n\nCordialement,',
      transferConfirmMessage: 'Cher/Chère {clientName},\n\nNous vous confirmons votre transfert {dateText} "{transferName}".\n\n✈️ Vol : {flightNumber}\n🕐 Heure : {time}\n👥 Nombre de passagers : {numberOfGuests}\n{meetingPointLine}\nToutes les autres informations de votre réservation restent inchangées. Si vous avez des questions, n\'hésitez pas à nous contacter.\n\nNous sommes impatients de vous accueillir à bord !\n\nCordialement,',
      paymentMessage: 'Bonjour,\n\nMerci, vous trouverez ci-dessous le lien de paiement afin de confirmer la réservation.\n\nhttps://jepaieenligne.systempay.fr/MASTERSKI_PILOU',
      invoiceMessage: 'Bonjour,\n\nMerci pour le contrat, voici la facture.\n\nBonne journée,',
      confirmationMessage: 'Bonjour,\n\nMerci pour le paiement, voici la confirmation.\n\nBonne journée,',
      vesselUpgradeMessage: 'Cher/Chère {clientName},\n\nNous avons le plaisir de vous annoncer que vous êtes surclassé sur le {vesselInfo} pour votre {serviceType} {route} {dateText}.\n\n{addressLine}Heure : {time}\nNombre de passagers : {numberOfGuests}\n\nToutes les autres informations de votre réservation restent inchangées. Si vous avez des questions, n\'hésitez pas à nous contacter.\n\nNous sommes impatients de vous accueillir à bord !\n\nCordialement,',
      jetskiMessage: 'Bonjour,\n\nMerci, vous trouverez ci-dessous notre lien de paiement afin de régler et confirmer votre réservation afin de réserver au moins un jetski.\n\nPrix pour {duration}: {price}€\n\nhttps://jepaieenligne.systempay.fr/MASTERSKI_PILOU\n\nBonne journée,',
      contractMessage: 'Bonjour,\n\nVeuillez bien vouloir trouver le contrat suivant et nous le retourner signé.\n\nBonne journée,',
      cashSheetMessage: 'Veuillez bien vouloir trouver ci-joint la feuille de caisse en date du {date}.\n\nBonne soirée,',
      cashSheetSubject: 'FEUILLE DE CAISSE {date}{invoices}',
      passportsMessage: 'Veuillez bien vouloir trouver ci-joint les passeports pour la journée du {date}.\n\nBonne journée,',
      passportsSubject: 'PASSEPORTS {date}',
      signature: 'Signature',
      vesselName: 'Nom du bateau',
      route: 'Trajet',
      from: 'De',
      to: 'Vers',
      clientName: 'Nom du client (optionnel)',
      date: 'Date',
      tomorrow: 'Demain',
      specificDate: 'Date spécifique',
      meetingPoint: '📍 Point de rendez-vous : 14 Rue Jeanne d\'Arc, Gustavia\n\n',
      customerType: 'Type de client',
      resident: 'Résident',
      tourist: 'Touriste',
      duration: 'Durée',
      customPrice: 'Prix personnalisé (optionnel)',
      recipientType: 'Type de destinataire',
      client: 'Client',
      agent: 'Agence',
      agentName: 'Nom de l\'agence',
      time: 'Heure',
      timeType: 'Type d\'heure',
      preset: 'Préréglé',
      custom: 'Personnalisé',
      serviceType: 'Type de service',
      transfer: 'Transfert',
      privateCharter: 'Location privée',
      numberOfGuests: 'Nombre de passagers',
      includeInvoices: 'Inclure FACTURES dans l\'objet',
      cashSheetEmailBody: 'Corps de l\'email',
      cashSheetEmailSubject: 'Objet de l\'email',
      askLunchInfo: 'Demander des informations sur le lunch',
      introPhrase: 'À bord vous aurez pour votre sortie :'
    },
    en: {
      title: 'Shortcuts',
      actions: 'Actions',
      boat: 'Boat',
      outingType: 'Outing Type',
      copyMessage: 'Copy Message',
      copied: 'Copied!',
      paymentAction: 'Payment Link',
      alcoholAction: 'Alcohol Package',
      invoiceAction: 'Send Invoice',
      confirmationAction: 'Payment Confirmation',
      vesselUpgradeAction: 'Vessel Upgrade',
      jetskiAction: 'Jetski Booking',
      contractAction: 'Send Contract',
      cashSheetAction: 'Cash Sheet',
      passportsAction: 'Passports',
      bookingConfirmAction: 'Booking Confirm',
      transferConfirmAction: 'Transfer Confirm',
      outingType: 'Outing type',
      fullDay: 'Full day',
      halfDayAM: 'Half-day morning',
      halfDayPM: 'Half-day afternoon',
      sunset: 'Sunset',
      bookingConfirmMessage: 'Dear {clientName},\n\nWe are pleased to confirm your {outingTypeText} {dateText} starting at {time} from:\n\n📍 Meeting Point: 14 Rue Jeanne d\'Arc, Gustavia\n\nTime: {time}\nNumber of guests: {numberOfGuests}{lunchQuestion}\nAll other details of your booking remain unchanged. Should you have any questions, please feel free to contact us.\n\n\nWe look forward to welcoming you aboard!\n\nBest regards,',
      transferConfirmMessage: 'Dear {clientName},\n\nWe confirm your transfer {dateText} "{transferName}".\n\n✈️ Flight: {flightNumber}\n🕐 Time: {time}\n👥 Number of guests: {numberOfGuests}\n{meetingPointLine}\nAll other details of your booking remain unchanged. Should you have any questions, please feel free to contact us.\n\nWe look forward to welcoming you aboard!\n\nBest regards,',
      paymentMessage: 'Hello,\n\nThank you, you can find below our payment link to pay and confirm your reservation.\n\nhttps://jepaieenligne.systempay.fr/MASTERSKI_PILOU',
      invoiceMessage: 'Hello,\n\nThank you for the contract, here is the invoice.\n\nBest regards,',
      confirmationMessage: 'Hello,\n\nThank you for the payment, here is the confirmation.\n\nBest regards,',
      vesselUpgradeMessage: 'Dear {clientName},\n\nWe are pleased to inform you that you have been upgraded to the {vesselInfo} for your {serviceType} {route} {dateText}.\n\n{addressLine}Time: {time}\nNumber of guests: {numberOfGuests}\n\nAll other details of your booking remain unchanged. Should you have any questions, please feel free to contact us.\n\nWe look forward to welcoming you aboard!\n\nBest regards,',
      jetskiMessage: 'Hello,\n\nThank you. Please find below our payment link to settle and confirm your booking — a payment for at least one jetski is required to secure the reservation:\n\nPrice for {duration}: €{price}\n\nhttps://jepaieenligne.systempay.fr/MASTERSKI_PILOU\n\nBest Regards,',
      contractMessage: 'Hello,\n\nPlease find the following contract and return it to us signed.\n\nBest regards,',
      cashSheetMessage: 'Please find attached the cash sheet dated {date}.\n\nBest regards,',
      cashSheetSubject: 'CASH SHEET {date}{invoices}',
      passportsMessage: 'Please find attached the passports for {date}.\n\nBest regards,',
      passportsSubject: 'PASSPORTS {date}',
      signature: 'Signature',
      vesselName: 'Vessel name',
      route: 'Route',
      from: 'From',
      to: 'To',
      clientName: 'Client name (optional)',
      date: 'Date',
      tomorrow: 'Tomorrow',
      specificDate: 'Specific date',
      meetingPoint: '📍 Meeting Point: 14 Rue Jeanne d\'Arc, Gustavia\n\n',
      customerType: 'Customer type',
      resident: 'Resident',
      tourist: 'Tourist',
      duration: 'Duration',
      customPrice: 'Custom price (optional)',
      recipientType: 'Recipient type',
      client: 'Client',
      agent: 'Agency',
      agentName: 'Agency name',
      time: 'Time',
      timeType: 'Time type',
      preset: 'Preset',
      custom: 'Custom',
      serviceType: 'Service type',
      transfer: 'Transfer',
      privateCharter: 'Private Charter',
      numberOfGuests: 'Number of guests',
      includeInvoices: 'Include INVOICES in subject',
      cashSheetEmailBody: 'Email body',
      cashSheetEmailSubject: 'Email subject',
      askLunchInfo: 'Ask for lunch information',
      introPhrase: 'On board you will have for your outing:'
    }
  };

  const t = translations[language];
  const signatures = ['Clementine', 'Felicie', 'Hannah', 'Laurie', 'Margaux', 'Pierre'];

  const getPaymentMessageWithSignature = () => `${t.paymentMessage}\n\nBest Regards,\n${selectedSignature}`;
  const getInvoiceMessageWithSignature = () => `${t.invoiceMessage}\n${selectedSignature}`;
  const getContractMessageWithSignature = () => `${t.contractMessage}\n${selectedSignature}`;

  const getConfirmationMessageWithSignature = () => {
    let message = t.confirmationMessage;
    if (confirmationData.askLunchInfo) {
      const lunchQuestion = language === 'fr' ? '\n\nAvez-vous des informations concernant les lunch des clients ?' : '\n\nDo you have any information about the clients\' lunch?';
      message = message + lunchQuestion;
    }
    return `${message}\n${selectedSignature}`;
  };

  const getVesselUpgradeMessageWithSignature = () => {
    let message = t.vesselUpgradeMessage;
    const route = vesselUpgradeData.serviceType === 'Private Charter' ? '' : `${vesselUpgradeData.departureFrom} - ${vesselUpgradeData.destinationTo}`;
    const clientName = vesselUpgradeData.clientName || '[Nom du client]';
    const selectedBoatObj = boats.find(b => b.name === vesselUpgradeData.vesselName);
    let vesselInfo = '[Bateau]';
    if (selectedBoatObj) {
      const brand = selectedBoatObj.brand || selectedBoatObj.name;
      const size = selectedBoatObj.length_feet ? `${selectedBoatObj.length_feet}ft` : '';
      vesselInfo = size ? `${brand} (${size})` : brand;
    }
    const time = vesselUpgradeData.timeType === 'preset' ? vesselUpgradeData.presetTime : vesselUpgradeData.customTime;
    let dateText;
    if (vesselUpgradeData.isTomorrow) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      dateText = language === 'fr' ? `du ${tomorrow.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}` : `on ${tomorrow.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
    } else {
      dateText = vesselUpgradeData.date || '[Date]';
    }
    const shouldShowAddress = vesselUpgradeData.serviceType === 'Private Charter' || vesselUpgradeData.departureFrom === 'SBH';
    const addressLine = shouldShowAddress ? t.meetingPoint : '';
    const serviceTypeText = vesselUpgradeData.serviceType === 'Private Charter' ? 'charter' : (language === 'fr' ? 'transfert' : 'transfer');
    message = message
      .replace('{clientName}', clientName)
      .replace('{route}', route)
      .replace('{vesselInfo}', vesselInfo)
      .replace('{dateText}', dateText)
      .replace('{time}', time || '[Heure]')
      .replace('{numberOfGuests}', vesselUpgradeData.numberOfGuests || '2')
      .replace('{serviceType}', serviceTypeText)
      .replace('{addressLine}', addressLine);
    if (confirmationData.askLunchInfo) {
      const lunchQuestion = language === 'fr' ? '\n\nAvez-vous des informations concernant les lunch des clients ?' : '\n\nDo you have any information about the clients\' lunch?';
      message = message + lunchQuestion;
    }
    return `${message}\n${selectedSignature}`;
  };

  const getJetskiMessageWithSignature = () => {
    let message = t.jetskiMessage;
    const pricing = {
      resident: { '1h': 100, '1h30': 130 },
      tourist: { '1h': 140, '1h30': 170 }
    };
    const price = jetskiData.customPrice || pricing[jetskiData.customerType][jetskiData.duration];
    const durationText = jetskiData.duration === '1h30' ? '1H30/island tour depending on sea condition' : (language === 'fr' ? '1H' : '1 Hour');
    message = message.replace('{duration}', durationText).replace('{price}', price);
    return `${message}\n${selectedSignature}`;
  };

  const getCashSheetMessageWithSignature = () => {
    const today = new Date();
    const dateStr = today.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    let message = t.cashSheetMessage.replace('{date}', dateStr);
    return `${message}\n${selectedSignature}`;
  };

  const getCashSheetSubject = () => {
    const today = new Date();
    const dateStr = today.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const invoicesText = cashSheetData.includeInvoices ? ' + FACTURES' : '';
    return t.cashSheetSubject.replace('{date}', dateStr).replace('{invoices}', invoicesText);
  };

  const getPassportsMessageWithSignature = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    let message = t.passportsMessage.replace('{date}', dateStr);
    return `${message}\n${selectedSignature}`;
  };

  const getPassportsSubject = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return t.passportsSubject.replace('{date}', dateStr);
  };

  // Booking Confirmation Message (for charter/sortie)
  const getBookingConfirmMessageWithSignature = () => {
    let message = t.bookingConfirmMessage;
    const clientName = bookingConfirmData.clientName || '[Nom du client]';

    // Get outing type text
    const outingTypeLabels = {
      fullDay: language === 'fr' ? 'journée complète' : 'full day charter',
      halfDayAM: language === 'fr' ? 'demi-journée matin' : 'half-day morning charter',
      halfDayPM: language === 'fr' ? 'demi-journée après-midi' : 'half-day afternoon charter',
      sunset: language === 'fr' ? 'sunset' : 'sunset charter'
    };
    const outingTypeText = outingTypeLabels[bookingConfirmData.outingType] || outingTypeLabels.fullDay;

    let dateText;
    if (bookingConfirmData.isTomorrow) {
      dateText = language === 'fr' ? 'demain' : 'tomorrow';
    } else if (bookingConfirmData.date) {
      const dateObj = dayjs(bookingConfirmData.date);
      dateText = language === 'fr'
        ? `le ${dateObj.format('D MMMM YYYY')}`
        : `on ${dateObj.format('MMMM D, YYYY')}`;
    } else {
      dateText = '[Date]';
    }
    // Generate lunch question text if enabled
    const lunchQuestionText = bookingConfirmData.askLunchInfo
      ? (language === 'fr'
          ? '\nAvez-vous des informations concernant les lunch des clients ?\n'
          : '\nDo you have any information about the clients\' lunch?\n')
      : '';

    message = message
      .replace('{clientName}', clientName)
      .replace('{outingTypeText}', outingTypeText)
      .replace(/{dateText}/g, dateText)
      .replace(/{time}/g, bookingConfirmData.time || '09:00')
      .replace('{numberOfGuests}', bookingConfirmData.numberOfGuests || '2')
      .replace('{lunchQuestion}', lunchQuestionText);

    return `${message}\n${selectedSignature}`;
  };

  // Transfer Confirmation Message
  const getTransferConfirmMessageWithSignature = () => {
    let message = t.transferConfirmMessage;
    const clientName = transferConfirmData.clientName || '[Nom du client]';
    const transferName = transferConfirmData.transferName || '[Nom du transfert]';
    const flightNumber = transferConfirmData.flightNumber || '[N° Vol]';
    let dateText;
    if (transferConfirmData.isTomorrow) {
      dateText = language === 'fr' ? 'demain' : 'tomorrow';
    } else if (transferConfirmData.date) {
      const dateObj = dayjs(transferConfirmData.date);
      dateText = language === 'fr'
        ? `le ${dateObj.format('D MMMM YYYY')}`
        : `on ${dateObj.format('MMMM D, YYYY')}`;
    } else {
      dateText = '[Date]';
    }
    const isSbhDeparture = transferConfirmData.departureFrom === 'SBH'
      || (transferName.toLowerCase().startsWith('sbh') || transferName.toLowerCase().startsWith('st barth'));
    const meetingPointLine = isSbhDeparture
      ? (language === 'fr' ? '\n📍 Point de rendez-vous : 14 Rue Jeanne d\'Arc, Gustavia - Masterski Pilou Office\n' : '\n📍 Meeting Point: 14 Rue Jeanne d\'Arc, Gustavia - Masterski Pilou Office\n')
      : '';
    message = message
      .replace('{clientName}', clientName)
      .replace('{transferName}', transferName)
      .replace('{flightNumber}', flightNumber)
      .replace(/{dateText}/g, dateText)
      .replace(/{time}/g, transferConfirmData.time || '09:00')
      .replace('{numberOfGuests}', transferConfirmData.numberOfGuests || '2')
      .replace('{meetingPointLine}', meetingPointLine);
    return `${message}\n${selectedSignature}`;
  };

  const formatPackageWithAlcoholNames = (packageText) => {
    if (!packageText) return '';
    return packageText
      .replace(/(\d+)\s*rosé/gi, '$1 Rosé Cuvée du Pearl')
      .replace(/(\d+)\s*champ(agne)?/gi, '$1 Champagne Bereche et Fils')
      .replace(/(\d+)\s*hk/gi, '$1 Heineken')
      .replace(/(\d+)\s*carib/gi, '$1 Carib');
  };

  const getBoatPackages = () => {
    if (!selectedBoat || !selectedBoat.charter_packages) return [];
    return selectedBoat.charter_packages.map(pkg => ({
      ...pkg,
      package: formatPackageWithAlcoholNames(pkg.package)
    }));
  };

  const boatPackages = getBoatPackages();
  const selectedPackage = boatPackages.find(pkg => pkg.type === selectedCharterType);
  const getFullPackageText = (packageText) => packageText ? `${t.introPhrase}\n${packageText}` : '';


  const getCurrentMessage = () => {
    if (selectedAction === 'payment') return getPaymentMessageWithSignature();
    if (selectedAction === 'invoice') return getInvoiceMessageWithSignature();
    if (selectedAction === 'confirmation') return getConfirmationMessageWithSignature();
    if (selectedAction === 'contract') return getContractMessageWithSignature();
    if (selectedAction === 'vesselUpgrade') return getVesselUpgradeMessageWithSignature();
    if (selectedAction === 'jetski') return getJetskiMessageWithSignature();
    if (selectedAction === 'bookingConfirm') return getBookingConfirmMessageWithSignature();
    if (selectedAction === 'transferConfirm') return getTransferConfirmMessageWithSignature();
    if (selectedAction === 'cashSheet') {
      const subject = getCashSheetSubject();
      const body = getCashSheetMessageWithSignature();
      return `${t.cashSheetEmailSubject}:\n${subject}\n\n${t.cashSheetEmailBody}:\n${body}`;
    }
    if (selectedAction === 'passports') {
      const subject = getPassportsSubject();
      const body = getPassportsMessageWithSignature();
      return `Objet:\n${subject}\n\nCorps de l'email:\n${body}`;
    }
    if (selectedAction === 'alcohol' && selectedPackage) return getFullPackageText(selectedPackage.package);
    return '';
  };

  const actionsConfig = [
    { id: 'bookingConfirm', label: t.bookingConfirmAction, icon: <CalendarOutlined /> },
    { id: 'transferConfirm', label: t.transferConfirmAction, icon: <CarOutlined /> },
    { id: 'vesselUpgrade', label: t.vesselUpgradeAction, icon: <RocketOutlined /> },
    { id: 'jetski', label: t.jetskiAction, icon: <ThunderboltOutlined /> }
  ];

  // Monochrome styles
  const styles = {
    page: { minHeight: '100vh', background: colors.bgPrimary, padding: '32px' },
    container: { maxWidth: '1400px', margin: '0 auto' },
    header: { marginBottom: '24px', paddingBottom: '20px', borderBottom: `1px solid ${colors.borderLight}` },
    headerContent: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
    headerIcon: { width: '48px', height: '48px', background: colors.bgDark, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' },
    headerTitle: { margin: 0, fontSize: '24px', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.5px' },
    headerSubtitle: { margin: 0, fontSize: '13px', color: colors.textTertiary, marginTop: '2px' },
    langBtn: (active) => ({ padding: '8px 16px', borderRadius: '10px', border: active ? 'none' : `1px solid ${colors.borderMedium}`, background: active ? colors.bgDark : colors.bgSecondary, color: active ? colors.textInverse : colors.textSecondary, fontWeight: 600, fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px' }),
    actionsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' },
    actionBtn: (active) => ({ cursor: 'pointer', background: active ? colors.bgDark : colors.bgSecondary, border: active ? 'none' : `1px solid ${colors.borderLight}`, borderRadius: '14px', transition: 'all 0.2s', padding: '16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', minHeight: '85px', color: active ? colors.textInverse : colors.textSecondary }),
    grid: { display: 'grid', gridTemplateColumns: '420px 1fr', gap: '24px' },
    card: { ...components.card },
    sectionLabel: { ...components.sectionLabel },
    fieldLabel: { ...components.fieldLabel },
    optionsBox: { ...components.optionsBox, marginBottom: '16px' },
    divider: { ...components.divider },
    previewContent: { ...components.previewContent, minHeight: '400px', maxHeight: '600px' },
    previewText: { ...components.previewText },
    primaryBtn: { ...components.primaryBtn },
  };

  if (loading) {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: colors.textPrimary }} spin />} />
          <p style={{ marginTop: '16px', color: colors.textTertiary, fontWeight: 500 }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.headerLeft}>
              <div style={styles.headerIcon}>⚡</div>
              <div>
                <h1 style={styles.headerTitle}>{t.title}</h1>
                <p style={styles.headerSubtitle}>Quick Message Templates</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setLanguage('fr')} style={styles.langBtn(language === 'fr')}>
                <GlobalOutlined /> FR
              </button>
              <button onClick={() => setLanguage('en')} style={styles.langBtn(language === 'en')}>
                <GlobalOutlined /> EN
              </button>
            </div>
          </div>
        </div>

        {/* Action Grid */}
        <div style={styles.actionsGrid}>
          {actionsConfig.map((action) => {
            const isSelected = selectedAction === action.id;
            return (
              <button
                key={action.id}
                onClick={() => setSelectedAction(action.id)}
                style={styles.actionBtn(isSelected)}
              >
                <div style={{ fontSize: '24px' }}>{action.icon}</div>
                <span style={{ fontSize: '11px', fontWeight: 600, lineHeight: '1.3' }}>
                  {action.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Two Column Layout */}
        <div style={styles.grid}>
          {/* Left Panel - Configuration */}
          <div style={styles.card}>
            {/* Signature */}
            <div style={{ marginBottom: '20px' }}>
              <div style={styles.sectionLabel}>✍️ {t.signature}</div>
              <Select value={selectedSignature} onChange={setSelectedSignature} style={{ width: '100%' }}>
                {signatures.map((sig) => <Option key={sig} value={sig}>{sig}</Option>)}
              </Select>
            </div>

            <div style={styles.divider} />

            {/* Alcohol Package Config */}
            {selectedAction === 'alcohol' && (
              <div style={styles.optionsBox}>
                <div style={{ ...styles.sectionLabel, marginBottom: '12px' }}>🍾 Alcohol Package</div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>🚤 {t.boat}</div>
                  <Select
                    value={selectedBoat?.id}
                    onChange={(value) => { const boat = boats.find(b => b.id === value); setSelectedBoat(boat); setSelectedCharterType(null); }}
                    style={{ width: '100%' }}
                  >
                    {boats.map((boat) => <Option key={boat.id} value={boat.id}>{boat.name}</Option>)}
                  </Select>
                </div>
                <div>
                  <div style={styles.fieldLabel}>⛵ {t.outingType}</div>
                  <Select
                    value={selectedCharterType}
                    onChange={setSelectedCharterType}
                    style={{ width: '100%' }}
                    disabled={!selectedBoat || boatPackages.length === 0}
                    placeholder={!selectedBoat ? "Select boat first" : "Outing type"}
                  >
                    {boatPackages.map((pkg, index) => <Option key={index} value={pkg.type}>{pkg.type}</Option>)}
                  </Select>
                </div>
              </div>
            )}

            {/* Vessel Upgrade Config */}
            {selectedAction === 'vesselUpgrade' && (
              <div style={{ ...styles.optionsBox, maxHeight: '500px', overflowY: 'auto' }}>
                <div style={{ ...styles.sectionLabel, marginBottom: '12px' }}>🚢 Vessel Upgrade</div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>👤 {t.recipientType}</div>
                  <Select value={vesselUpgradeData.recipientType} onChange={(value) => setVesselUpgradeData({ ...vesselUpgradeData, recipientType: value })} style={{ width: '100%' }}>
                    <Option value="client">{t.client}</Option>
                    <Option value="agent">{t.agent}</Option>
                  </Select>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>✏️ {vesselUpgradeData.recipientType === 'client' ? t.clientName : t.agentName}</div>
                  <Input
                    value={vesselUpgradeData.recipientType === 'client' ? vesselUpgradeData.clientName : vesselUpgradeData.agentName}
                    onChange={(e) => setVesselUpgradeData({ ...vesselUpgradeData, [vesselUpgradeData.recipientType === 'client' ? 'clientName' : 'agentName']: e.target.value })}
                    placeholder="Full name"
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>⚓ {t.serviceType}</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setVesselUpgradeData({ ...vesselUpgradeData, serviceType: 'Transfer' })} style={segmentBtn(vesselUpgradeData.serviceType === 'Transfer')}>{t.transfer}</button>
                    <button onClick={() => setVesselUpgradeData({ ...vesselUpgradeData, serviceType: 'Private Charter' })} style={segmentBtn(vesselUpgradeData.serviceType === 'Private Charter')}>{t.privateCharter}</button>
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>🛥️ {t.vesselName}</div>
                  <Select value={vesselUpgradeData.vesselName} onChange={(value) => setVesselUpgradeData({ ...vesselUpgradeData, vesselName: value })} style={{ width: '100%' }} showSearch>
                    {boats.map((boat) => <Option key={boat.id} value={boat.name}>{boat.name} ({boat.length_feet}ft)</Option>)}
                  </Select>
                </div>

                {vesselUpgradeData.serviceType === 'Transfer' && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={styles.fieldLabel}>🗺️ {t.route}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <Select value={vesselUpgradeData.departureFrom} onChange={(value) => setVesselUpgradeData({ ...vesselUpgradeData, departureFrom: value })} style={{ width: '100%' }} placeholder={t.from}>
                        {locations.map((loc) => <Option key={loc.code} value={loc.code}>{loc.name}</Option>)}
                      </Select>
                      <Select value={vesselUpgradeData.destinationTo} onChange={(value) => setVesselUpgradeData({ ...vesselUpgradeData, destinationTo: value })} style={{ width: '100%' }} placeholder={t.to}>
                        {locations.map((loc) => <Option key={loc.code} value={loc.code}>{loc.name}</Option>)}
                      </Select>
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>📅 {t.date}</div>
                  <Radio.Group value={vesselUpgradeData.isTomorrow} onChange={(e) => setVesselUpgradeData({ ...vesselUpgradeData, isTomorrow: e.target.value })} style={{ marginBottom: '8px', display: 'block' }}>
                    <Radio value={true} style={{ fontSize: '12px' }}>{t.tomorrow}</Radio>
                    <Radio value={false} style={{ fontSize: '12px' }}>{t.specificDate}</Radio>
                  </Radio.Group>
                  {!vesselUpgradeData.isTomorrow && (
                    <DatePicker value={vesselUpgradeData.date ? dayjs(vesselUpgradeData.date) : null} onChange={(date) => setVesselUpgradeData({ ...vesselUpgradeData, date: date ? date.format('YYYY-MM-DD') : '' })} format="DD/MM/YYYY" style={{ width: '100%' }} />
                  )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>🕐 {t.time}</div>
                  {vesselUpgradeData.timeType === 'preset' ? (
                    <Select value={vesselUpgradeData.presetTime} onChange={(value) => setVesselUpgradeData({ ...vesselUpgradeData, presetTime: value })} style={{ width: '100%' }}>
                      <Option value="09:00">09:00</Option>
                      <Option value="13:00">13:00</Option>
                      <Option value="17:00">17:00</Option>
                    </Select>
                  ) : (
                    <Input type="time" value={vesselUpgradeData.customTime} onChange={(e) => setVesselUpgradeData({ ...vesselUpgradeData, customTime: e.target.value })} style={{ width: '100%' }} />
                  )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>👥 {t.numberOfGuests}</div>
                  <Input type="number" value={vesselUpgradeData.numberOfGuests} onChange={(e) => setVesselUpgradeData({ ...vesselUpgradeData, numberOfGuests: e.target.value })} min="1" />
                </div>

                <Checkbox checked={confirmationData.askLunchInfo} onChange={(e) => setConfirmationData({ ...confirmationData, askLunchInfo: e.target.checked })} style={{ fontSize: '12px' }}>
                  🍽️ {t.askLunchInfo}
                </Checkbox>
              </div>
            )}

            {/* Jetski Config */}
            {selectedAction === 'jetski' && (
              <div style={styles.optionsBox}>
                <div style={{ ...styles.sectionLabel, marginBottom: '12px' }}>🏍️ Jetski Booking</div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>👤 {t.customerType}</div>
                  <Select value={jetskiData.customerType} onChange={(value) => setJetskiData({ ...jetskiData, customerType: value })} style={{ width: '100%' }}>
                    <Option value="resident">{t.resident}</Option>
                    <Option value="tourist">{t.tourist}</Option>
                  </Select>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>⏱️ {t.duration}</div>
                  <Select value={jetskiData.duration} onChange={(value) => setJetskiData({ ...jetskiData, duration: value })} style={{ width: '100%' }}>
                    <Option value="1h">1H</Option>
                    <Option value="1h30">1H30 / Island Tour</Option>
                  </Select>
                </div>
                <div>
                  <div style={styles.fieldLabel}>💰 {t.customPrice}</div>
                  <Input type="number" value={jetskiData.customPrice} onChange={(e) => setJetskiData({ ...jetskiData, customPrice: e.target.value })} suffix="€" placeholder="Default price if empty" />
                </div>
              </div>
            )}

            {/* Cash Sheet Config */}
            {selectedAction === 'cashSheet' && (
              <div style={styles.optionsBox}>
                <div style={{ ...styles.sectionLabel, marginBottom: '12px' }}>📄 Cash Sheet</div>
                <Checkbox checked={cashSheetData.includeInvoices} onChange={(e) => setCashSheetData({ ...cashSheetData, includeInvoices: e.target.checked })} style={{ fontSize: '12px' }}>
                  {t.includeInvoices}
                </Checkbox>
              </div>
            )}

            {/* Confirmation Config */}
            {selectedAction === 'confirmation' && (
              <div style={styles.optionsBox}>
                <div style={{ ...styles.sectionLabel, marginBottom: '12px' }}>✅ Confirmation</div>
                <Checkbox checked={confirmationData.askLunchInfo} onChange={(e) => setConfirmationData({ ...confirmationData, askLunchInfo: e.target.checked })} style={{ fontSize: '12px' }}>
                  🍽️ {t.askLunchInfo}
                </Checkbox>
              </div>
            )}

            {/* Booking Confirmation Config (Sortie) */}
            {selectedAction === 'bookingConfirm' && (
              <div style={styles.optionsBox}>
                <div style={{ ...styles.sectionLabel, marginBottom: '12px' }}>📅 Confirmation Sortie</div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>✏️ {t.clientName}</div>
                  <Input
                    value={bookingConfirmData.clientName}
                    onChange={(e) => setBookingConfirmData({ ...bookingConfirmData, clientName: e.target.value })}
                    placeholder="Nom du client"
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>⛵ {t.outingType}</div>
                  <Select
                    value={bookingConfirmData.outingType}
                    onChange={(value) => setBookingConfirmData({ ...bookingConfirmData, outingType: value })}
                    style={{ width: '100%' }}
                  >
                    <Option value="fullDay">{t.fullDay}</Option>
                    <Option value="halfDayAM">{t.halfDayAM}</Option>
                    <Option value="halfDayPM">{t.halfDayPM}</Option>
                    <Option value="sunset">{t.sunset}</Option>
                  </Select>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>📅 {t.date}</div>
                  <Radio.Group
                    value={bookingConfirmData.isTomorrow}
                    onChange={(e) => setBookingConfirmData({ ...bookingConfirmData, isTomorrow: e.target.value })}
                    style={{ marginBottom: '8px', display: 'block' }}
                  >
                    <Radio value={true} style={{ fontSize: '12px' }}>{t.tomorrow}</Radio>
                    <Radio value={false} style={{ fontSize: '12px' }}>{t.specificDate}</Radio>
                  </Radio.Group>
                  {!bookingConfirmData.isTomorrow && (
                    <DatePicker
                      value={bookingConfirmData.date ? dayjs(bookingConfirmData.date) : null}
                      onChange={(date) => setBookingConfirmData({ ...bookingConfirmData, date: date ? date.format('YYYY-MM-DD') : '' })}
                      format="DD/MM/YYYY"
                      style={{ width: '100%' }}
                    />
                  )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>🕐 {t.time}</div>
                  <Select
                    value={bookingConfirmData.time}
                    onChange={(value) => setBookingConfirmData({ ...bookingConfirmData, time: value })}
                    style={{ width: '100%' }}
                  >
                    <Option value="09:00">09:00</Option>
                    <Option value="13:00">13:00</Option>
                    <Option value="17:00">17:00</Option>
                  </Select>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>👥 {t.numberOfGuests}</div>
                  <Input
                    type="number"
                    value={bookingConfirmData.numberOfGuests}
                    onChange={(e) => setBookingConfirmData({ ...bookingConfirmData, numberOfGuests: e.target.value })}
                    min="1"
                  />
                </div>

                <Checkbox
                  checked={bookingConfirmData.askLunchInfo}
                  onChange={(e) => setBookingConfirmData({ ...bookingConfirmData, askLunchInfo: e.target.checked })}
                  style={{ fontSize: '12px' }}
                >
                  🍽️ {t.askLunchInfo}
                </Checkbox>
              </div>
            )}

            {/* Transfer Confirmation Config */}
            {selectedAction === 'transferConfirm' && (
              <div style={styles.optionsBox}>
                <div style={{ ...styles.sectionLabel, marginBottom: '12px' }}>🚗 Confirmation Transfert</div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>✏️ {t.clientName}</div>
                  <Input
                    value={transferConfirmData.clientName}
                    onChange={(e) => setTransferConfirmData({ ...transferConfirmData, clientName: e.target.value })}
                    placeholder="Nom du client"
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>🗺️ Direction</div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Select
                      value={transferConfirmData.departureFrom || undefined}
                      onChange={(value) => {
                        const toValue = transferConfirmData.transferName?.split(' - ')[1] || '';
                        const loc = locations.find(l => l.code === value);
                        setTransferConfirmData({ ...transferConfirmData, departureFrom: value, transferName: `${loc?.name || value} - ${toValue}` });
                      }}
                      placeholder="From"
                      style={{ flex: 1 }}
                    >
                      {locations.map(loc => <Option key={loc.code} value={loc.code}>{loc.code}</Option>)}
                    </Select>
                    <span style={{ color: '#999' }}>→</span>
                    <Select
                      value={transferConfirmData.arrivalTo || undefined}
                      onChange={(value) => {
                        const fromLoc = locations.find(l => l.code === transferConfirmData.departureFrom);
                        const toLoc = locations.find(l => l.code === value);
                        setTransferConfirmData({ ...transferConfirmData, arrivalTo: value, transferName: `${fromLoc?.name || transferConfirmData.departureFrom || ''} - ${toLoc?.name || value}` });
                      }}
                      placeholder="To"
                      style={{ flex: 1 }}
                    >
                      {locations.map(loc => <Option key={loc.code} value={loc.code}>{loc.code}</Option>)}
                    </Select>
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>✏️ Nom du transfert</div>
                  <Input
                    value={transferConfirmData.transferName}
                    onChange={(e) => setTransferConfirmData({ ...transferConfirmData, transferName: e.target.value })}
                    placeholder="Ex: SXM - SBH"
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>✈️ Numéro de vol</div>
                  <Input
                    value={transferConfirmData.flightNumber}
                    onChange={(e) => setTransferConfirmData({ ...transferConfirmData, flightNumber: e.target.value })}
                    placeholder="Ex: AF123"
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>📅 {t.date}</div>
                  <Radio.Group
                    value={transferConfirmData.isTomorrow}
                    onChange={(e) => setTransferConfirmData({ ...transferConfirmData, isTomorrow: e.target.value })}
                    style={{ marginBottom: '8px', display: 'block' }}
                  >
                    <Radio value={true} style={{ fontSize: '12px' }}>{t.tomorrow}</Radio>
                    <Radio value={false} style={{ fontSize: '12px' }}>{t.specificDate}</Radio>
                  </Radio.Group>
                  {!transferConfirmData.isTomorrow && (
                    <DatePicker
                      value={transferConfirmData.date ? dayjs(transferConfirmData.date) : null}
                      onChange={(date) => setTransferConfirmData({ ...transferConfirmData, date: date ? date.format('YYYY-MM-DD') : '' })}
                      format="DD/MM/YYYY"
                      style={{ width: '100%' }}
                    />
                  )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={styles.fieldLabel}>🕐 {t.time}</div>
                  <TimePicker
                    value={transferConfirmData.time ? dayjs(transferConfirmData.time, 'HH:mm') : null}
                    onChange={(time) => setTransferConfirmData({ ...transferConfirmData, time: time ? time.format('HH:mm') : '09:00' })}
                    format="HH:mm"
                    style={{ width: '100%' }}
                    minuteStep={5}
                    placeholder="Sélectionner l'heure"
                  />
                </div>

                <div>
                  <div style={styles.fieldLabel}>👥 {t.numberOfGuests}</div>
                  <Input
                    type="number"
                    value={transferConfirmData.numberOfGuests}
                    onChange={(e) => setTransferConfirmData({ ...transferConfirmData, numberOfGuests: e.target.value })}
                    min="1"
                  />
                </div>
              </div>
            )}

          </div>

          {/* Right Panel - Preview */}
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: colors.bgDark, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: colors.textInverse }}>
                {actionsConfig.find(a => a.id === selectedAction)?.icon || <CopyOutlined />}
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>Message Preview</h3>
              </div>
            </div>

            <pre style={styles.previewContent}>
              <code style={styles.previewText}>
                {getCurrentMessage() || 'Select an action and configure settings'}
              </code>
            </pre>

            <button
              onClick={() => { const message = getCurrentMessage(); if (message) copyToClipboard(message, selectedAction); }}
              disabled={!getCurrentMessage()}
              style={{ ...styles.primaryBtn, marginTop: '16px', opacity: getCurrentMessage() ? 1 : 0.5 }}
            >
              {copiedItem === selectedAction ? <><CheckOutlined /> {t.copied}</> : <><CopyOutlined /> {t.copyMessage}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shortcuts;
